import {
  DocketDisplayItem,
  DocketDisplayItemType,
} from '../../../common/types';
import { PaginatedDocketItems } from '../../layoutHelper';
import {
  getDocketItems,
  isDocketLarge,
  splitDocketIntoChunks,
  wouldOverflowColumn,
} from './docketUtils';
import {
  createCell,
  ensureGridExists,
  finalizeGrid,
  convertGridToOutput,
} from './gridUtils';
import {
  markBottomOnlyColumns,
  handleColumnGaps,
  findNextAvailablePosition,
  findNextPosition,
} from './positionUtils';
import { Grid, PositionInfo } from './types';

export const getTwoRowPaginatedDocketItems = (
  items: DocketDisplayItem[],
  scale: (n: number) => number,
  columnHeight: number,
  prevItems: PaginatedDocketItems[],
  colsPerPage: number,
): PaginatedDocketItems[] => {
  const rowHeight = columnHeight / 2;
  const positionsPerPage = colsPerPage * 2;

  // Optimize grid initialization with direct assignment
  const grids: Grid[] = [
    {
      page: 1,
      columns: [],
    },
  ];

  // Create initial columns with direct loop instead of fill().map()
  const initialColumns = new Array(colsPerPage);
  for (let colIndex = 0; colIndex < colsPerPage; colIndex++) {
    initialColumns[colIndex] = [
      createCell(colIndex, 0, 0, positionsPerPage),
      createCell(colIndex, 1, 0, positionsPerPage),
    ];
  }
  grids[0].columns = initialColumns;

  // Process docket headers in a single pass
  const currentDocketIds: string[] = [];
  const docketHeaderIndices: Record<string, number> = {};

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type === DocketDisplayItemType.DOCKET_HEADER && 'id' in item) {
      const docketId = item.id as string;
      currentDocketIds.push(docketId);
      docketHeaderIndices[docketId] = i;
    }
  }

  // Initialize data structures for positioning
  const positionMap: Record<string, PositionInfo> = {};
  const bottomOnlyColumns = new Set<string>();
  const usedColumns = new Set<string>();
  const docketOverflows = new Map<
    string,
    Array<{ page: number; column: number }>
  >();
  const existingDocketIds = new Set<string>();

  // Process previous layout information in a single pass
  if (prevItems && prevItems.length > 0) {
    for (let pageIndex = 0; pageIndex < prevItems.length; pageIndex++) {
      const page = prevItems[pageIndex];
      if (!page || !page.columns) continue;

      const pageNum = pageIndex + 1;

      for (let colIndex = 0; colIndex < page.columns.length; colIndex++) {
        const column = page.columns[colIndex];
        if (!column || !column.rows) continue;

        const colNum = column.column;
        const colKey = `${pageNum}-${colNum}`;
        usedColumns.add(colKey);

        // Track headers in this column
        let hasTopHeader = false;
        let hasBottomHeader = false;

        for (let rowIndex = 0; rowIndex < column.rows.length; rowIndex++) {
          const row = column.rows[rowIndex];
          if (!row || !row.items) continue;

          for (let itemIndex = 0; itemIndex < row.items.length; itemIndex++) {
            const item = row.items[itemIndex];
            if (!item || item.type !== DocketDisplayItemType.DOCKET_HEADER)
              continue;

            if ('id' in item) {
              const docketId = item.id as string;
              existingDocketIds.add(docketId);

              // Track docket overflow positions
              let positions = docketOverflows.get(docketId);
              if (!positions) {
                positions = [];
                docketOverflows.set(docketId, positions);
              }
              positions.push({ page: pageNum, column: colNum });

              // Track row positions
              const rowPosition = item?.position?.rowPosition || 0;

              if (rowPosition === 1) {
                hasTopHeader = true;
              } else if (rowPosition === 2) {
                hasBottomHeader = true;
              }

              // Update position map for current dockets
              if (currentDocketIds.includes(docketId)) {
                positionMap[docketId] = {
                  page: pageNum,
                  column: colNum,
                  rowPosition: rowPosition,
                };
              }
            }
          }
        }

        // Mark bottom-only columns
        if (hasBottomHeader && !hasTopHeader) {
          bottomOnlyColumns.add(colKey);
        }
      }
    }
  }

  // Handle column gaps (assumed to be an external function)
  handleColumnGaps(
    prevItems,
    usedColumns,
    positionMap,
    bottomOnlyColumns,
    docketOverflows,
  );

  // Process dockets - combine multiple operations
  const dockets = [];

  for (let i = 0; i < currentDocketIds.length; i++) {
    const docketId = currentDocketIds[i];
    const headerIndex = docketHeaderIndices[docketId];

    // Get all items for this docket in one pass
    const docketItems = getDocketItems(items, docketId, headerIndex);

    // Calculate key properties
    const isLarge = isDocketLarge(docketItems, rowHeight, scale);
    const maxHeight = isLarge ? columnHeight : rowHeight;
    const needsSplitting = wouldOverflowColumn(docketItems, maxHeight, scale);

    // Split into chunks if needed
    const chunks = needsSplitting
      ? splitDocketIntoChunks(docketItems, maxHeight, rowHeight, scale)
      : [docketItems];

    dockets.push({
      id: docketId,
      items: docketItems,
      isLarge,
      orderNumber: items[headerIndex].orderNumber,
      chunks,
    });
  }

  // Mark bottom-only columns
  markBottomOnlyColumns(grids, bottomOnlyColumns, colsPerPage);

  // Place dockets on grid
  const placedDocketIds = new Set<string>();

  for (let docketIndex = 0; docketIndex < dockets.length; docketIndex++) {
    const docket = dockets[docketIndex];
    if (!docket.chunks?.length) continue;

    for (let chunkIndex = 0; chunkIndex < docket.chunks.length; chunkIndex++) {
      const chunk = docket.chunks[chunkIndex];
      const isFirstChunk = chunkIndex === 0;

      // Calculate chunk properties once
      let needsFullColumn = false;
      let chunkHeight = 0;

      for (let i = 0; i < chunk.length; i++) {
        const item = chunk[i];
        chunkHeight += scale(item.height);

        if (item?.position?.needsFullColumn === true) {
          needsFullColumn = true;
        }
      }

      const isActuallyTall = chunkHeight > rowHeight;

      // Find position for this chunk
      const positionInfo = isFirstChunk
        ? findNextPosition(
            grids,
            positionMap,
            colsPerPage,
            positionsPerPage,
            docketOverflows,
            bottomOnlyColumns,
            docket.isLarge,
            docket.id,
          )
        : findNextAvailablePosition(
            grids,
            colsPerPage,
            positionsPerPage,
            bottomOnlyColumns,
            isActuallyTall,
            needsFullColumn,
          );

      const { pageIndex, colIndex, rowIndex, rowPosition } = positionInfo;

      placedDocketIds.add(docket.id);

      // Ensure grid exists and update cell
      ensureGridExists(grids, pageIndex, colIndex, colsPerPage);
      const cell = grids[pageIndex].columns[colIndex][rowIndex];

      // Add items directly without spread operator
      for (let i = 0; i < chunk.length; i++) {
        cell.items.push(chunk[i]);
      }

      cell.isOccupied = true;
      cell.forDocketId = docket.id;

      // Update header position if present
      if (
        chunk.length > 0 &&
        chunk[0].type === DocketDisplayItemType.DOCKET_HEADER
      ) {
        chunk[0].position = chunk[0].position || {};
        chunk[0].position.rowPosition = rowPosition;

        if (
          rowPosition === 2 &&
          !grids[pageIndex].columns[colIndex][0].isOccupied
        ) {
          bottomOnlyColumns.add(`${pageIndex + 1}-${colIndex + 1}`);
          grids[pageIndex].columns[colIndex][0].isReserved = true;
        }
      }

      // Update positions for all items
      for (let i = 0; i < chunk.length; i++) {
        const item = chunk[i];
        if (
          item.type !== DocketDisplayItemType.DOCKET_HEADER &&
          'docketId' in item
        ) {
          item.position = item.position || {};
          item.position.parentRowPosition = rowPosition;
        }
      }

      // Handle tall items
      if ((isActuallyTall || needsFullColumn) && rowIndex === 0) {
        const bottomCell = grids[pageIndex].columns[colIndex][1];
        bottomCell.isTall = true;
        bottomCell.isOccupied = true;
        bottomCell.forDocketId = docket.id;
      }
    }
  }

  finalizeGrid(grids);

  return convertGridToOutput(grids, colsPerPage);
};
